import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import ChatBot from '@/components/ChatBot';
import EnquiryPopup from '@/components/EnquiryPopup';
import PageEffects from '@/components/PageEffects';
import ProjectSlider from '@/components/ProjectSlider';
import { Button } from '@/components/ui/button';
import {
  Building2,
  Store,
  ShoppingBag,
  LayoutGrid,
  Map,
  Users,
  Layers,
  ArrowRight,
  Shield,
  Award,
  TrendingUp,
  Phone,
  Volume2,
  VolumeX,
  Search,
  MapPin,
  Ruler,
  IndianRupee,
  Flame,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEnquiry, setShowEnquiry] = useState(false);
  const videoRef = useRef(null);
  const [heroSettings, setHeroSettings] = useState({
    headline: 'Premium Commercial',
    headline_accent: 'Properties in Gurgaon',
    subheadline: "Discover high-value investment opportunities in India's fastest-growing commercial hub. Office spaces, retail, SCO plots & more.",
    tagline: 'Exclusive Commercial Properties',
    media_type: 'image',
    media_url: 'https://images.unsplash.com/photo-1549851501-a521e1e3fb85?w=1920',
    button_primary_text: 'Explore Properties',
    button_secondary_text: 'Schedule Consultation',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propertiesRes, heroRes, projectsRes] = await Promise.all([
          axios.get(`${API}/properties/featured`),
          axios.get(`${API}/settings/hero`),
          axios.get(`${API}/projects/homepage`),
        ]);
        setFeaturedProperties(propertiesRes.data);
        setHeroSettings(heroRes.data);
        setFeaturedProjects(projectsRes.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Toggle video sound
  const toggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Helper function to get video MIME type based on file extension
  const getVideoMimeType = (url) => {
    if (!url) return 'video/mp4';
    const extension = url.split('.').pop()?.toLowerCase();
    const mimeTypes = {
      'mp4': 'video/mp4',
      'mov': 'video/quicktime',
      'webm': 'video/webm',
      'avi': 'video/x-msvideo',
      'mkv': 'video/x-matroska',
      'ogv': 'video/ogg',
    };
    return mimeTypes[extension] || 'video/mp4';
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('http')) {
      try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/api/uploads')) {
          return `${process.env.REACT_APP_BACKEND_URL}${parsed.pathname}`;
        }
      } catch (e) {}
      return url;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const categories = [
    { id: 'office', name: 'Office Space', icon: Building2, count: '50+' },
    { id: 'retail', name: 'Retail Shops', icon: Store, count: '30+' },
    { id: 'mall_shop', name: 'Mall Shops', icon: ShoppingBag, count: '25+' },
    { id: 'sco', name: 'SCO Plots', icon: LayoutGrid, count: '40+' },
    { id: 'commercial_plot', name: 'Commercial Plots', icon: Map, count: '20+' },
    { id: 'coworking', name: 'Co-working', icon: Users, count: '15+' },
    { id: 'mixed_use', name: 'Mixed Use', icon: Layers, count: '10+' },
  ];

  const sectors = [
    { name: 'Golf Course Road', properties: '25+', highlight: true },
    { name: 'Cyber City', properties: '30+', highlight: true },
    { name: 'MG Road', properties: '20+', highlight: false },
    { name: 'Sector 29', properties: '15+', highlight: false },
    { name: 'Dwarka Expressway', properties: '35+', highlight: true },
    { name: 'Sohna Road', properties: '18+', highlight: false },
  ];

  const developers = [
    'DLF Limited',
    'M3M India',
    'Godrej Properties',
    'Emaar India',
    'BPTP',
    'Vatika Group',
  ];

  const stats = [
    { value: '200+', label: 'Properties Listed' },
    { value: '₹500 Cr+', label: 'Worth of Deals' },
    { value: '1000+', label: 'Happy Clients' },
    { value: '15+', label: 'Years Experience' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#faf7f0' }} data-testid="home-page">
      <Header />
      <PageEffects />

      {/* Hero Section - Admin Editable Photo/Video */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        data-testid="hero-section"
      >
        {/* Background Media from Admin */}
        {heroSettings.media_type === 'video' && !videoError ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            key={resolveUrl(heroSettings.media_url)}
            onError={() => setVideoError(true)}
            onCanPlay={() => setVideoError(false)}
            src={resolveUrl(heroSettings.media_url)}
            type="video/mp4"
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: heroSettings.media_type === 'video' && videoError
                ? `url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920)`
                : `url(${resolveUrl(heroSettings.media_url)})`
            }}
          />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,22,40,0.85) 0%, rgba(10,22,40,0.5) 40%, rgba(26,14,0,0.7) 100%)' }} />

        {/* Sound Toggle */}
        {heroSettings.media_type === 'video' && !videoError && (
          <button
            onClick={toggleSound}
            className="absolute bottom-32 left-8 z-20 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(201,168,76,0.3)', backdropFilter: 'blur(8px)' }}
            data-testid="sound-toggle"
          >
            {isMuted ? <VolumeX className="w-6 h-6 text-white/80" /> : <Volume2 className="w-6 h-6 text-white/80" />}
          </button>
        )}

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <p className="sec-tag mb-4" style={{ WebkitTextFillColor: '#d4a828' }}>
              {heroSettings.tagline}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {heroSettings.headline}
              <br />
              <span style={{ color: '#d4a828' }}>{heroSettings.headline_accent}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-10" style={{ fontFamily: "'Tenor Sans', sans-serif" }}>
              {heroSettings.subheadline}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  navigate(`/properties${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`);
                }}
                className="flex items-center overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 50, backdropFilter: 'blur(8px)' }}
                data-testid="hero-search-form"
              >
                <Search className="w-5 h-5 ml-5 flex-shrink-0" style={{ color: 'rgba(201,168,76,0.5)' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by project, sector, or location..."
                  className="flex-1 px-4 py-4 text-sm border-none outline-none bg-transparent"
                  style={{ color: '#fdf4e0', fontFamily: "'Tenor Sans', sans-serif" }}
                  data-testid="hero-search-input"
                />
                <button
                  type="submit"
                  className="px-8 py-4 text-sm font-semibold uppercase tracking-wider transition-colors text-white"
                  style={{ background: 'linear-gradient(135deg,#8a6a10,#c9a84c)', fontFamily: "'Josefin Sans', sans-serif" }}
                  data-testid="hero-search-button"
                >
                  Search
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center pt-2" style={{ borderColor: 'rgba(201,168,76,0.3)' }}>
            <div className="w-1 h-2 rounded-full" style={{ background: 'rgba(201,168,76,0.5)', animation: 'scrollDot 2s ease infinite' }} />
          </div>
          <span className="mt-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(201,168,76,0.4)', fontFamily: "'Josefin Sans', sans-serif" }}>Scroll</span>
        </div>
      </section>

      {/* Ticker Strip */}
      <div className="ticker-strip">
        <div className="ticker-inner">
          {'Premium Commercial \u25C6 Sector 65 Gurugram \u25C6 M3M Route 65 \u25C6 Office Space \u25C6 Retail Shops \u25C6 SCO Plots \u25C6 High ROI Investment \u25C6 DLF Cyber City \u25C6 Golf Course Road \u25C6 RERA Registered \u25C6 '
            .repeat(3)}
        </div>
      </div>

      {/* Stats Bar - Deep Navy */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg, #0a1628, #142240)', borderBottom: '3px solid #c9a84c' }} data-testid="stats-section" data-stats>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <p className="text-3xl md:text-4xl font-bold stat-num">
                  {stat.value}
                </p>
                <p className="text-sm text-white/60 uppercase tracking-wider mt-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24" style={{ background: '#fff' }} data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="sec-tag mb-4">
              Browse By Type
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2a1e00]">
              Property Categories
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={category.id}
                  to={`/properties/${category.id}`}
                  className="category-card p-6 bg-white text-center group hover-lift border border-[rgba(201,168,76,0.2)] hover:border-[#c9a84c]/30"
                  data-testid={`category-${category.id}`}
                >
                  <IconComponent className="w-8 h-8 mx-auto mb-4 text-[#5a4a20] category-icon transition-colors group-hover:text-[#c9a84c]" />
                  <h3 className="text-sm font-bold mb-1 text-[#2a1e00]">{category.name}</h3>
                  <p className="text-xs text-[#8a7a50]">{category.count} Properties</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Builder Projects Section - Slider */}
      <section className="py-24" style={{ background: '#faf7f0' }} data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="sec-tag mb-4">
              Top Developments
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#2a1e00' }}>
              Builder Projects
            </h2>
            <p className="text-sm mt-3" style={{ color: '#8a7a50', fontFamily: "'Tenor Sans', sans-serif" }}>
              {featuredProjects.length} projects across Gurgaon
            </p>
            <div className="section-divider mx-auto mt-6" />
          </div>

          {featuredProjects.length > 0 ? (
            <ProjectSlider projects={featuredProjects} onEnquire={() => setShowEnquiry(true)} />
          ) : (
            <p className="text-center" style={{ color: '#8a7a50' }}>Projects coming soon. Stay tuned!</p>
          )}
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-24 bg-white" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="sec-tag mb-4">
                Handpicked For You
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2a1e00]">
                Featured Properties
              </h2>
            </div>
            <Link to="/properties" className="mt-6 md:mt-0">
              <Button className="btn-secondary" data-testid="view-all-properties">
                View All Properties
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#fdf9f0] p-4 animate-pulse rounded-xl">
                  <div className="h-48 bg-[rgba(201,168,76,0.2)] mb-4 rounded-lg" />
                  <div className="h-4 bg-[rgba(201,168,76,0.2)] mb-2 w-3/4 rounded" />
                  <div className="h-4 bg-[rgba(201,168,76,0.2)] w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.slice(0, 6).map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  variant="featured"
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sectors Section - Rich Teal */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #0a2a2a, #1a3a3a)' }} data-testid="sectors-section" data-loc-section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="sec-tag mb-4">
              Prime Locations
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Explore By Sector
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectors.map((sector, index) => (
              <Link
                key={sector.name}
                to="/properties"
                className="loc-card group p-8 transition-all duration-300 hover-lift rounded-xl"
                style={{
                  background: sector.highlight ? 'linear-gradient(135deg, #c9a84c, #8a6a10)' : 'rgba(201,168,76,0.08)',
                  border: `1px solid ${sector.highlight ? 'transparent' : 'rgba(201,168,76,0.2)'}`,
                }}
                data-testid={`sector-${index}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">{sector.name}</h3>
                    <p className="text-sm text-white/60">
                      {sector.properties} Properties
                    </p>
                  </div>
                  <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2 text-white/70" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section - Deep Charcoal */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #1c1c1e, #2a2a2e)' }} data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="sec-tag mb-4">
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Your Trusted Partner in Commercial Real Estate
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#c9a84c] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">
                      Verified Properties
                    </h4>
                    <p className="text-[#8a7a50] text-sm">
                      All properties are thoroughly verified with clear documentation
                      and legal compliance.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#c9a84c] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">
                      RERA Registered
                    </h4>
                    <p className="text-[#8a7a50] text-sm">
                      We deal only with RERA-compliant properties for your peace of
                      mind.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#c9a84c] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">
                      Investment Advisory
                    </h4>
                    <p className="text-[#8a7a50] text-sm">
                      Expert guidance on high-yield commercial investments in
                      Gurgaon&apos;s prime locations.
                    </p>
                  </div>
                </div>
              </div>

              <Link to="/contact" className="inline-block mt-10">
                <Button className="btn-primary" data-testid="contact-us-btn">
                  Contact Us
                  <Phone className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Developer Logos */}
            <div className="bg-white/5 p-12 rounded-2xl">
              <p className="text-center text-sm text-white/60 uppercase tracking-wider mb-8">
                Partnered with Leading Developers
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                {developers.map((developer) => (
                  <div
                    key={developer}
                    className="bg-white/10 p-4 flex items-center justify-center h-20 rounded-lg"
                  >
                    <p className="text-white/80 font-medium text-center text-sm">
                      {developer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Deep Navy + Gold */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #0a1628, #1a2a48, #0a1628)' }} data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ready to Invest in Gurgaon Commercial Real Estate?
          </h2>
          <p className="text-lg text-white/70 mb-10" style={{ fontFamily: "'Tenor Sans', sans-serif" }}>
            Get personalized property recommendations from our experts. Schedule a free
            consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="px-8 py-4 rounded-lg transition-all duration-300 tracking-widest uppercase text-xs font-bold shadow-lg"
              style={{ background: 'linear-gradient(135deg,#c9a84c,#d4a828)', color: '#fff', fontFamily: "'Josefin Sans', sans-serif" }}
              data-testid="schedule-call-btn"
              onClick={() => setShowEnquiry(true)}
            >
              Schedule a Call
            </button>
            <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer">
              <button className="px-8 py-4 rounded-lg transition-all duration-300 tracking-widest uppercase text-xs font-bold border"
                style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontFamily: "'Josefin Sans', sans-serif" }}>
                WhatsApp Us
              </button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* ChatBot */}
      <ChatBot />

      {/* Enquiry Popup */}
      <EnquiryPopup open={showEnquiry} onClose={() => setShowEnquiry(false)} />
    </div>
  );
};

export default HomePage;
